import { useEffect, useState, useCallback } from "react";
import { CalendarClock, Flag, Plus } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const DOT = { green: "bg-success-500", amber: "bg-warning-500", red: "bg-danger-500", grey: "bg-muted-foreground/40" };
const TXT = { green: "text-success-700", amber: "text-warning-700", red: "text-danger-700", grey: "text-muted-foreground" };
const LABEL = { green: "On track", amber: "At risk", red: "Off track", grey: "Not measured" };

export default function ScorecardPanel({ changeId }) {
  const { can } = useAuth();
  const [sc, setSc] = useState(null);
  const load = useCallback(() => api.get(`/changes/${changeId}/scorecard`).then((r) => setSc(r.data)).catch(() => setSc(null)), [changeId]);
  useEffect(() => { load(); }, [load]);
  if (!sc) return null;

  const track = async (ln) => {
    const text = window.prompt(`Track “${ln.label}” as a condition someone owns. Describe what must be true:`, `${ln.label} resolved before go-live`);
    if (!text) return;
    const due = window.prompt("Due date (YYYY-MM-DD), optional:", sc.go_live ? sc.go_live.slice(0, 10) : "") || null;
    try { await api.post(`/changes/${changeId}/conditions`, { text, due, line: ln.key }); toast.success("Tracked as a condition"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const dl = sc.days_to_golive;
  return (
    <section className="space-y-4" data-testid="scorecard-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-head text-lg font-extrabold text-foreground">Go-live readiness scorecard</h3>
          <p className="text-sm text-muted-foreground">Are people ready to go live on the planned date? As at {sc.as_at.slice(0, 16).replace("T", " ")}.</p>
        </div>
        <div className="flex items-center gap-3">
          {dl !== null && dl !== undefined && (
            <div className={`rounded-xl border px-3 py-2 text-center ${dl < 0 ? "border-danger-200 bg-danger-50" : "border-border bg-card"}`} data-testid="scorecard-days">
              <div className="text-xl font-extrabold text-foreground flex items-center gap-1"><CalendarClock size={16} className="text-accent-600" /> {dl < 0 ? `${-dl} overdue` : dl}</div>
              <div className="text-xs text-muted-foreground">{dl < 0 ? "days past go-live" : "days to go-live"}</div>
            </div>
          )}
          <span data-testid="scorecard-headline" className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-bold ${TXT[sc.headline]}`}><span className={`h-2.5 w-2.5 rounded-full ${DOT[sc.headline]}`} /> {LABEL[sc.headline]}</span>
        </div>
      </div>
      <div className="rounded-xl border border-border divide-y divide-border" data-testid="scorecard-lines">
        {sc.lines.map((ln) => (
          <div key={ln.key} data-testid={`scorecard-line-${ln.key}`} className="flex items-start gap-3 p-3">
            <span title={LABEL[ln.status]} className={`mt-1 h-3 w-3 rounded-full shrink-0 ${DOT[ln.status]}`} data-testid={`scorecard-status-${ln.key}-${ln.status}`} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-bold text-foreground">{ln.label}</span>
                <span className="text-xs text-muted-foreground" title={ln.threshold} data-testid={`scorecard-tip-${ln.key}`}>ⓘ</span>
                {ln.status === "grey" && <span className="text-xs font-semibold text-muted-foreground">not measured</span>}
              </div>
              <div className="text-sm text-body">{ln.detail}</div>
            </div>
            {can("write") && ln.status !== "green" && ln.status !== "grey" && (
              <button type="button" onClick={() => track(ln)} data-testid={`scorecard-track-${ln.key}`} className="pp-btn-ghost text-xs py-1 shrink-0"><Flag size={12} /> Track</button>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}
